(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-prohealth-popup-root]';
  const POPUP_SELECTOR = '[data-prohealth-popup]';
  const collectionProductCache = new Map();
  let activePopup = null;
  let previousFocus = null;

  const splitList = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

  function storageKey(popup) {
    return `prohealthPromoPopup:${popup.dataset.campaignId}`;
  }

  function frequencyAllows(popup, designMode) {
    if (designMode) return true;
    const frequency = popup.dataset.frequency || 'session';
    const key = storageKey(popup);
    if (frequency === 'always') return true;
    if (frequency === 'session') return sessionStorage.getItem(key) !== 'shown';

    const lastShown = Number(localStorage.getItem(key) || 0);
    if (!lastShown) return true;
    const day = 24 * 60 * 60 * 1000;
    const elapsed = Date.now() - lastShown;
    return frequency === 'day' ? elapsed >= day : elapsed >= day * 7;
  }

  function markShown(popup, designMode) {
    if (designMode) return;
    const frequency = popup.dataset.frequency || 'session';
    const key = storageKey(popup);
    if (frequency === 'session') sessionStorage.setItem(key, 'shown');
    if (frequency === 'day' || frequency === 'week') localStorage.setItem(key, String(Date.now()));
  }

  function openPopup(popup, designMode) {
    if (!popup || activePopup === popup || popup.dataset.enabled !== 'true') return;
    if (!frequencyAllows(popup, designMode)) return;
    if (activePopup) closePopup(activePopup);

    previousFocus = document.activeElement;
    popup.classList.add('is-open');
    document.body.classList.add('prohealth-promo-popup-open');
    activePopup = popup;
    markShown(popup, designMode);

    const dialog = popup.querySelector('.prohealth-promo-popup__dialog');
    if (dialog) requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
  }

  function closePopup(popup) {
    if (!popup) return;
    popup.classList.remove('is-open');
    if (activePopup === popup) activePopup = null;
    document.body.classList.remove('prohealth-promo-popup-open');
    if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus({ preventScroll: true });
    previousFocus = null;
  }

  function popupHasVisitTarget(popup, context) {
    const productIds = splitList(popup.dataset.targetProductIds);
    const collectionHandles = splitList(popup.dataset.targetCollectionHandles);
    if (context.productId && productIds.includes(String(context.productId))) return true;
    if (context.collectionHandle && collectionHandles.includes(context.collectionHandle)) return true;
    return context.productCollections.some((handle) => collectionHandles.includes(handle));
  }

  async function collectionContainsProduct(collectionHandle, productId) {
    if (!collectionHandle || !productId) return false;
    if (!collectionProductCache.has(collectionHandle)) {
      collectionProductCache.set(collectionHandle, (async () => {
        try {
          const response = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) return new Set();
          const data = await response.json();
          return new Set((data.products || []).map((product) => String(product.id)));
        } catch (error) {
          return new Set();
        }
      })());
    }
    const productIds = await collectionProductCache.get(collectionHandle);
    return productIds.has(String(productId));
  }

  async function popupMatchesAddedProduct(popup, productId) {
    if (!productId) return false;
    const productIds = splitList(popup.dataset.targetProductIds);
    if (productIds.includes(String(productId))) return true;
    const collectionHandles = splitList(popup.dataset.targetCollectionHandles);
    for (const handle of collectionHandles) {
      if (await collectionContainsProduct(handle, productId)) return true;
    }
    return false;
  }

  function scheduleOpen(popup, designMode) {
    const delay = Math.max(0, Number(popup.dataset.delay || 0)) * 1000;
    window.setTimeout(() => openPopup(popup, designMode), delay);
  }

  function extractProductId(payload) {
    if (!payload) return null;
    if (Array.isArray(payload.items) && payload.items.length) return payload.items[0].product_id || payload.items[0].productId || null;
    return payload.product_id || payload.productId || null;
  }

  function initRoot(root) {
    if (root.dataset.prohealthPopupInitialised === 'true') return;
    root.dataset.prohealthPopupInitialised = 'true';

    const designMode = root.dataset.designMode === 'true';
    const context = {
      productId: root.dataset.currentProductId && root.dataset.currentProductId !== '0' ? root.dataset.currentProductId : null,
      collectionHandle: root.dataset.currentCollectionHandle || '',
      productCollections: splitList(root.dataset.currentProductCollections)
    };
    const popups = Array.from(root.querySelectorAll(POPUP_SELECTOR));

    popups.forEach((popup) => {
      popup.querySelectorAll('[data-prohealth-popup-close]').forEach((button) => button.addEventListener('click', () => closePopup(popup)));
      const overlay = popup.querySelector('[data-prohealth-popup-overlay]');
      if (overlay && popup.dataset.closeOverlay === 'true') overlay.addEventListener('click', () => closePopup(popup));
      const trigger = popup.dataset.trigger;
      if ((trigger === 'product_visit' || trigger === 'both') && popupHasVisitTarget(popup, context)) scheduleOpen(popup, designMode);
    });

    document.addEventListener('prohealth:product-added', async (event) => {
      const productId = event.detail && event.detail.productId;
      if (!productId) return;
      for (const popup of popups) {
        const trigger = popup.dataset.trigger;
        if (trigger !== 'add_to_cart' && trigger !== 'both') continue;
        if (popup.dataset.enabled !== 'true' || !frequencyAllows(popup, designMode)) continue;
        if (await popupMatchesAddedProduct(popup, productId)) {
          scheduleOpen(popup, designMode);
          break;
        }
      }
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activePopup) closePopup(activePopup);
  });

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
      if (requestUrl && /\/cart\/add(?:\.js)?(?:\?|$)/.test(requestUrl) && response.ok) {
        const clone = response.clone();
        clone.json().then((payload) => {
          const productId = extractProductId(payload);
          if (productId) document.dispatchEvent(new CustomEvent('prohealth:product-added', { detail: { productId } }));
        }).catch(() => {});
      }
    } catch (error) {}
    return response;
  };

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('DOMContentLoaded', initAll);
  if (document.readyState !== 'loading') initAll();
})();
